import { format } from 'date-fns';
import { Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { FileAttachment } from './FileAttachment';
import { toast } from 'sonner';
import type { Message } from '@/lib/api/services/chat.service';

interface MessageBubbleProps {
  message: Message;
  isOwn: boolean;
  senderName?: string;
  isLastMessage?: boolean;
}

export function MessageBubble({
  message,
  isOwn,
  senderName,
  isLastMessage = false,
}: MessageBubbleProps) {
  const time = format(new Date(message.created_at), 'h:mm a');
  const [copied, setCopied] = useState(false);
  const [showCopyButton, setShowCopyButton] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      toast.success('Message copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (_error) {
      toast.error('Failed to copy message');
    }
  };

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`max-w-[70%] flex gap-2 ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
        {!isOwn && (
          <Avatar className="h-8 w-8 mt-1">
            <AvatarFallback className="text-xs font-semibold bg-slate-200 text-slate-600">
              {senderName
                ?.split(' ')
                .map((n) => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2) || 'U'}
            </AvatarFallback>
          </Avatar>
        )}
        <div className="flex flex-col">
          {!isOwn && senderName && (
            <p className="text-xs mb-1 text-slate-500">
              {senderName}
            </p>
          )}
          <div
            className={`px-4 py-3 rounded-2xl ${
              isOwn
                ? 'bg-[#4669ed] text-white rounded-br-md'
                : 'bg-slate-100 text-slate-800 rounded-bl-md'
            }`}
          >
            {/* Show text content if it exists */}
            {message.content && (
              <p className={`text-[15px] leading-relaxed whitespace-pre-wrap ${isOwn ? 'text-white' : ''} ${message.file_url ? 'mb-2' : ''}`}>
                {message.content}
              </p>
            )}
            {/* Show file attachment if it exists */}
            {message.file_url && (
              <FileAttachment
                fileUrl={message.file_url}
                fileName={message.file_name || 'File'}
                fileType={message.file_type || 'application/octet-stream'}
                caption={message.caption || null}
                isOwn={isOwn}
              />
            )}
          </div>
          <div
            className={`flex items-center gap-1 mt-1 ${isOwn ? 'justify-end' : 'justify-start'} relative`}
            onMouseEnter={() => isLastMessage && setShowCopyButton(true)}
            onMouseLeave={() => setShowCopyButton(false)}
          >
            <p className="text-xs text-slate-400">
              {time}
            </p>
            {isLastMessage && showCopyButton && message.message_type === 'text' && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 ml-1"
                onClick={handleCopy}
                title="Copy message"
              >
                {copied ? (
                  <Check className="h-3 w-3" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
